import React, { useState, useEffect, useContext } from 'react';
import {
  Card,
  Stack,
  Button,
  Drawer,
  Box,
  Chip,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  LISTING_COLUMNS,
  LISTING_ACTIONS,
} from '../../config/module-configs/content-playlists.config';
import Iconify from '../common/iconify.component';
import {
  getListData,
  deleteDataById,
  getSubPlaylists,
} from '../../services/playlists.service';
import { ACTIONS } from '../../config/const.config';
import SnackbarInfo from '../common/snackbar-info.component';
import ConfirmPopup from '../common/confirm-popup.component';
import ViewContentPlaylist from './view-content-playlist.component';
import FilterContentPlaylists from './filter-content-playlists.component';
import ListSequencePlaylists from './sequence-content-playlist.component';
import { FiltersContext } from '../../context/filter.context';
import useAccess from '../../hooks/access.hook';
import useFilteredActions from '../../hooks/filtered-action.hook';
import ListPlaylistData from '../table-elements/list-playlist-data.component';
import CELL_DATA_TYPES from '../../config/cell-types.config';
import ListSequenceSubPlaylists from './sequence-sub-playlist.component';
import {
  getLocalFilterValue,
  setLocalFilterValue,
} from '../../utils/local-cache.util';
import useAbortableRequest from '../../hooks/abortable-request.hook';
import ROUTE_PATH from '../../config/routes.config';
import replaceIdUrl from '../../utils/url.util';

const ListContentPlaylists = () => {
  const FILTERS_KEY = 'playlists-filters';
  const columns = LISTING_COLUMNS;
  const actions = useFilteredActions('playlists', LISTING_ACTIONS);
  const { getSignal, isAbortError } = useAbortableRequest();

  const navigate = useNavigate();
  const { hasOperationAccess } = useAccess();

  const [options, setOptions] = useState({
    page: 0,
    rowsPerPage: 100,
    totalRows: 0,
    rows: [],
    reloadCounter: 0,
    sortBy: '',
    sortOrder: '',
    error: false,
    loading: true,
    appliedFilters: {
      search: { name: 'Search', value: '' },
      channels: { name: 'Channels', value: [] },
      playlistLevel: { name: 'Playlist level', value: 1 },
      videoSource: { name: 'Video Source', value: '' },
    },
    filtersCounter: 0,
  });
  const [doAction, setDoAction] = useState({
    data: null,
    action: null,
  });
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });
  const { openFilter, handleToggleFilters } = useContext(FiltersContext);
  const [openManageSequence, setOpenManageSequence] = useState(false);

  const handleShowSubRow = (rowData) => {
    setOptions((prevOptions) => ({
      ...prevOptions,
      rows: prevOptions.rows.map((item) =>
        item.id === rowData.id
          ? { ...item, isExpanded: !item.isExpanded }
          : item
      ),
    }));

    // If subRows already exist, don't call API
    if (rowData?.subRows && rowData.subRows.length > 0) {
      return;
    }

    // Set loading state while fetching data
    setOptions((prevOptions) => ({
      ...prevOptions,
      rows: prevOptions.rows.map((item) =>
        item.id === rowData.id
          ? { ...item, subRowsLoading: true, subRows: [] }
          : item
      ),
    }));

    // Fetch sub-playlists
    getSubPlaylists(rowData.id)
      .then((res) => {
        setOptions((prevOptions) => ({
          ...prevOptions,
          rows: prevOptions.rows.map((item) =>
            item.id === rowData.id
              ? { ...item, subRowsLoading: false, subRows: res?.data || [] }
              : item
          ),
        }));
      })
      .catch(() => {
        setOptions((prevOptions) => ({
          ...prevOptions,
          rows: prevOptions.rows.map((item) =>
            item.id === rowData.id ? { ...item, subRowsLoading: false } : item
          ),
        }));
      });
  };

  const handlePageChange = (v) => {
    setOptions({
      ...options,
      page: v,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };
  const handleRowsPerPageChange = (v) => {
    setOptions({
      ...options,
      rowsPerPage: v,
      page: 0,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };
  const handleSortingChange = (sortBy, sortOrder) => {
    setOptions({
      ...options,
      sortBy,
      sortOrder,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };
  const handleRefreshData = () => {
    setOptions({
      ...options,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };

  const handleApplyFilters = (selectedFilters) => {
    setLocalFilterValue(FILTERS_KEY, selectedFilters);
    let counter = 0;
    if (selectedFilters.search.value.trim()) counter += 1;
    if (selectedFilters.channels.value.length > 0) counter += 1;
    if (selectedFilters.playlistLevel.value) counter += 1;

    setOptions({
      ...options,
      appliedFilters: {
        ...selectedFilters,
      },
      filtersCounter: counter,
      page: 0,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };
  const handleClearFilters = () => {
    setLocalFilterValue(FILTERS_KEY, {});
    setOptions({
      ...options,
      appliedFilters: {
        search: { name: 'Search', value: '' },
        channels: { name: 'Channels', value: [] },
        playlistLevel: { name: 'Playlist level', value: 1 },
      },
      filtersCounter: 0,
      page: 0,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };

  const handleActionSelect = (selectedAction, selectedData) => {
    if (selectedAction === ACTIONS.CREATE.value) {
      navigate(ROUTE_PATH.CONTENT_ALL_PLAYLISTS_CREATE);
      return;
    }
    if (selectedAction === ACTIONS.EDIT.value) {
      const url = replaceIdUrl(
        ROUTE_PATH.CONTENT_ALL_PLAYLISTS_EDIT,
        selectedData.id
      );
      navigate(url);
      return;
    }
    setDoAction({
      ...doAction,
      data: selectedData || null,
      action: selectedAction || null,
    });
  };
  const handleActionCancel = () => {
    setDoAction({
      ...doAction,
      data: null,
      action: null,
    });
  };

  const handleActionSuccess = (message = '') => {
    if (message) {
      setSnackbarInfo({
        ...snackbarInfo,
        show: true,
        type: 'success',
        message,
      });
    }

    handleActionCancel();
    handleRefreshData();
  };

  const handleSequenceNotification = (message = '', type = '') => {
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });
    if (message && type) {
      setTimeout(() => {
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type,
          message,
        });
      }, 10);
    }
  };

  const handleDeleteData = () => {
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });

    deleteDataById(doAction.data.id)
      .then(() => {
        handleActionSuccess('Playlist deleted successfully.');
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        handleActionCancel();
      });
  };

  const handleDeleteFilter = (field) => {
    if (field === 'clear-all') {
      handleClearFilters();
      return;
    }
    const updatedFilters = { ...options.appliedFilters };
    updatedFilters[field] = {
      ...updatedFilters[field],
      value: Array.isArray(updatedFilters[field]?.value) ? [] : null || '',
    };
    setLocalFilterValue(FILTERS_KEY, updatedFilters);

    setOptions({
      ...options,
      appliedFilters: updatedFilters,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };

  useEffect(() => {
    const signal = getSignal();

    const localFilters = getLocalFilterValue(FILTERS_KEY);

    const params = [];
    params.push(`page=${options.page + 1}`);
    params.push(`perPage=${options.rowsPerPage}`);
    if (options.sortBy && options.sortOrder) {
      params.push(`sortBy=${options.sortBy}`);
      params.push(`sortOrder=${options.sortOrder}`);
    }
    const appliedFilters =
      Object.keys(localFilters).length > 0
        ? localFilters
        : options.appliedFilters;
    const filters = Object.keys(appliedFilters).reduce((acc, key) => {
      acc[key] = appliedFilters[key].value;
      return acc;
    }, {});
    if (filters.search) {
      params.push(`q=${encodeURIComponent(filters.search)}`);
    }
    if (filters.channels.length > 0) {
      const channels = [];
      filters.channels.forEach((c) => channels.push(c.id));
      params.push(`channels=${channels.join(',')}`);
    }
    if (filters.playlistLevel) {
      params.push(`level=${filters.playlistLevel}`);
    }

    if (filters.videoSource) {
      params.push(`videoSource=${filters.videoSource}`);
    }

    const uri = '';
    const paramsQuery = params.length > 0 ? `?${params.join('&')}` : '';
    getListData(uri + paramsQuery, signal)
      .then((res) => {
        if (!signal.aborted) {
          setOptions({
            ...options,
            loading: false,
            totalRows: res?.data?.totalRows || 0,
            rows: res?.data?.rows || [],
            appliedFilters: appliedFilters || {},
            error: false,
          });
        }
      })
      .catch((error) => {
        if (isAbortError(error)) {
          return;
        }

        setOptions({
          ...options,
          loading: false,
          page: 0,
          totalRows: 0,
          rows: [],
          error: true,
        });
      });
  }, [options.reloadCounter]);

  return (
    <Box
      sx={{
        transition: 'margin 0.3s ease, width 0.3s ease',
        width: openFilter ? 'calc(100% - 300px)' : '100%',
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{ px: 0, py: 2 }}
      >
        <div>
          {hasOperationAccess('playlists', 'create') && (
            <Button
              color="primary"
              variant="contained"
              startIcon={<Iconify icon="ic:round-add" />}
              onClick={() => handleActionSelect(ACTIONS.CREATE.value, null)}
            >
              Create New
            </Button>
          )}
        </div>
        <div>
          {hasOperationAccess('playlists', 'manage') && (
            <Button
              color="inherit"
              variant="contained"
              startIcon={<Iconify icon="fe:list-order" />}
              sx={{ marginRight: 2 }}
              onClick={() => {
                setOpenManageSequence(true);
              }}
            >
              Manage Order
            </Button>
          )}
          <Button
            color="inherit"
            variant="contained"
            startIcon={<Iconify icon="ic:twotone-refresh" />}
            onClick={handleRefreshData}
            disabled={options.loading}
          >
            Refresh
          </Button>
        </div>
      </Stack>

      {options.appliedFilters &&
        Object.entries(options.appliedFilters).some(
          ([key, filter]) =>
            ((Array.isArray(filter.value) && filter.value.length > 0) ||
              (!Array.isArray(filter.value) &&
                filter.value !== '' &&
                filter.value !== null)) &&
            key !== 'playlistLevel'
        ) && (
          <Box mb={2}>
            <Typography variant="body1" fontWeight="bold" display="inline">
              Filters Applied:{' '}
              <Chip
                key="all"
                label="Clear all"
                onDelete={() => handleDeleteFilter('clear-all')}
                sx={{ ml: 1, color: 'red' }}
              />
            </Typography>
            {Object.entries(options.appliedFilters)
              .filter(
                ([key, filter]) =>
                  ((Array.isArray(filter.value) && filter.value.length > 0) ||
                    (!Array.isArray(filter.value) &&
                      filter.value !== '' &&
                      filter.value !== null)) &&
                  key !== 'playlistLevel'
              )
              .map(([field, filter]) => (
                <Chip
                  key={field}
                  label={filter.name || 'Active'}
                  onDelete={() => handleDeleteFilter(field)}
                  sx={{ ml: 1 }}
                />
              ))}
          </Box>
        )}

      <Card>
        <ListPlaylistData
          columns={
            options.appliedFilters.playlistLevel.value === 2
              ? [
                  {
                    id: 'fields',
                    label: '',
                    width: '2%',
                    align: 'center',
                    dataKey: 'fields',
                    sortable: false,
                    type: CELL_DATA_TYPES.SUB_LIST,
                  },
                  ...columns,
                ]
              : columns
          }
          rows={options.rows}
          page={options.page}
          rowsPerPage={options.rowsPerPage}
          totalRows={options.totalRows}
          loading={options.loading}
          actions={actions}
          error={options.error}
          sortBy={options.sortBy}
          sortOrder={options.sortOrder}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          onSortChange={handleSortingChange}
          onAction={handleActionSelect}
          subRowColumns={[
            {
              id: 'image',
              label: '',
              width: '5%',
              align: 'center',
              dataKey: 'image',
              sortable: false,
              type: CELL_DATA_TYPES.LIST_IMAGE,
            },
            ...columns.filter((col) => col.id === 'name'),
            ...columns.filter(
              (col) => col.id !== 'channels' && col.id !== 'name'
            ),
          ]}
          handleShowSubRow={handleShowSubRow}
        />
      </Card>

      <Drawer
        anchor="right"
        open={openFilter}
        variant="persistent"
        PaperProps={{
          sx: { width: 300, border: 'none', overflow: 'hidden' },
        }}
      >
        <FilterContentPlaylists
          values={options.appliedFilters}
          onCancel={handleToggleFilters}
          onSuccess={handleApplyFilters}
          onClear={handleClearFilters}
        />
      </Drawer>

      {doAction.action === ACTIONS.VIEW.value && (
        <ViewContentPlaylist
          title={`Playlist Details - ${doAction.data.name}`}
          dataId={doAction.data.id}
          onCancel={handleActionCancel}
        />
      )}

      {doAction.action === ACTIONS.DELETE.value && (
        <ConfirmPopup
          title="Delete Playlist"
          message={`Do you want to delete ${doAction.data.name} playlist? You can not undo this action!`}
          onCancel={handleActionCancel}
          onSuccess={() => handleDeleteData()}
        />
      )}

      {doAction.action === ACTIONS.MANAGE_ORDER.value && (
        <ListSequenceSubPlaylists
          title="Reorder Playlists"
          parentPlaylistId={doAction.data.id}
          handleCloseDialog={handleActionCancel}
          handleSequenceNotification={handleSequenceNotification}
        />
      )}

      {openManageSequence && (
        <ListSequencePlaylists
          title="Reorder Playlists"
          handleCloseDialog={() => setOpenManageSequence(false)}
          handleSequenceNotification={handleSequenceNotification}
        />
      )}

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </Box>
  );
};

export default ListContentPlaylists;
